import { getArchetypes, getBaseThemeName, getConfig, getDefaultInputValues } from "@/features/branding";
import { ArchetypeCategory, defaultBuildYear } from "@/features/risk_bands";
import {
  portfolioExportExampleAssets,
  portfolioTemplateLeadAsset,
} from "@/tools/aggregate/portfolio-export/portfolio_template_assets";
import { keys } from "@/tools/aggregate/portfolio-export/schema";

export type PortfolioExportExampleHeaderRow = [
  string?,
  string?,
  string?,
  string?,
  string?,
  string?,
  string?,
  string?,
  string?,
  string?,
  string?,
  string?,
  string?,
  string?,
  string?,
  string?,
  string?,
];
type PortfolioExportExampleBodyRow = [
  string, // ID
  string?, // Address
  string?, // Latitude
  string?, // Longitude
  number?, // Elevation
  string?, // Archetype
  number?, // Build Year
  number?, // Floor Height
  number?, // Market Value
  number?, // Replacement Cost
  number?, // Mortgage Rate
  number?, // Heat Threshold Temperature
  number?, // Heat Threshold Return Frequency
  number?, // Extreme Wind Speed Threshold Return Frequency
  number?, // Tropical Cyclone Wind Threshold Speed
  string?, // Fire Protection
  string?, // Foundation Design
];
type PortfolioExportExampleLines = [PortfolioExportExampleHeaderRow, ...Array<PortfolioExportExampleBodyRow>];

export type PortfolioExportExampleKey = keyof typeof keys;

type PortfolioExportExample = {
  id: string;
  address?: string;
  latitude?: string;
  longitude?: string;
  elevation?: number;
  archetype?: string;
  buildYear?: number;
  floorHeight?: number;
  marketValue?: number;
  replacementCost?: number;
  mortgageRate?: number;

  heatThresholdReturnFrequency?: number;
  heatThresholdReturnPeriod?: number;
  heatThresholdTemperature?: number;

  windThresholdReturnFrequency?: number;
  windThresholdReturnPeriod?: number;

  tcwThresholdReturnFrequency?: number;
  tcwThresholdReturnPeriod?: number;
  tcwThresholdWindSpeed?: number;

  fireProtection?: string;
  foundationDesign?: string;
};

const buildLineRow = (columns: string[], data: PortfolioExportExample) => {
  return columns.map((key) => (key in data ? data[key as keyof PortfolioExportExample] : undefined));
};

type UploadTemplate = ReturnType<typeof getConfig>["portfolioExport"]["templates"]["global"];
type Archetype = ReturnType<typeof getArchetypes>[number];

const headerRow = (columns: PortfolioExportExampleKey[]) =>
  columns.map((column) => keys[column]) as PortfolioExportExampleHeaderRow;

/** Columns derived from an archetype, shared by the lead rows and the per-archetype rows. */
const archetypeInputs = (archetype: Archetype | undefined, regionType?: string, pricedAs?: string) => ({
  archetype: archetype?.name,
  buildYear: archetype?.defaultBuildYear || defaultBuildYear,
  floorHeight: archetype?.defaultHeightAboveGround || 0,
  ...getDefaultInputValues(pricedAs ?? archetype?.value, regionType),
});

const archetypeRow = (
  columns: PortfolioExportExampleKey[],
  archetype: Archetype,
  {
    id,
    lat,
    lng,
    regionType,
    pricedAs,
  }: { id: string; lat: string; lng: string; regionType?: string; pricedAs?: string }
) =>
  buildLineRow(columns, {
    id,
    latitude: lat,
    longitude: lng,
    ...archetypeInputs(archetype, regionType, pricedAs),
  }) as PortfolioExportExampleBodyRow;

/** prefix segment for userSuppliedId per-archetype row. */
const categoryLabels: Record<ArchetypeCategory, string> = {
  residential: "Residential",
  commercial: "Commercial",
  industrial: "Infrastructure",
};

/** column order generated by advanced (standard = subset) template */
const columnOrder: PortfolioExportExampleKey[] = [
  "id",
  "address",
  "latitude",
  "longitude",
  "archetype",
  "buildYear",
  "floorHeight",
  "marketValue",
  "replacementCost",
  "mortgageRate",
  "elevation",
  "fireProtection",
  "foundationDesign",
  "heatThresholdReturnPeriod",
  "heatThresholdReturnFrequency",
  "heatThresholdTemperature",
  "windThresholdReturnPeriod",
  "windThresholdReturnFrequency",
  "tcwThresholdReturnPeriod",
  "tcwThresholdReturnFrequency",
  "tcwThresholdWindSpeed",
];

/** Spaces archetypes without a sample coordinate on a grid around the region centre. */
const gridCoordinates = (template: UploadTemplate, index: number, total: number) => {
  const perRow = Math.floor(Math.sqrt(total));
  return {
    lat: (template.centre.lat - 0.005 * (index % perRow)).toFixed(4),
    lng: (template.centre.lng - 0.005 * Math.floor(index / perRow)).toFixed(4),
  };
};

/**
 * HKSAR template: geocoded address samples in three scripts, then one row per archetype
 * around the region centre.
 */
const buildHkLines = (
  columns: PortfolioExportExampleKey[],
  archetypes: Archetype[],
  regionType: string | undefined,
  template: UploadTemplate
): PortfolioExportExampleLines => {
  const latitude = template.centre.lat.toFixed(4);
  const longitude = template.centre.lng.toFixed(4);

  const lines: PortfolioExportExampleLines = [
    headerRow(columns),
    buildLineRow(columns, {
      id: "XDI Geocode Generated LatLong from address: English",
      address: template.address,
      fireProtection: "none",
      foundationDesign: "normal",
    }) as PortfolioExportExampleBodyRow,
    buildLineRow(columns, {
      id: "XDI Geocode Generated LatLong from address: Simplified Chinese",
      address: "香港中环遮打道10号太子大厦",
    }) as PortfolioExportExampleBodyRow,
    buildLineRow(columns, {
      id: "XDI Geocode Generated LatLong from address: Traditional Chinese",
      address: "香港中環遮打道10號太子大廈",
    }) as PortfolioExportExampleBodyRow,
    buildLineRow(columns, {
      id: "Customer Provided LatLong",
      latitude,
      longitude,
      ...template.defaultSettings,
      fireProtection: "heatEmber",
      foundationDesign: "rigid",
    }) as PortfolioExportExampleBodyRow,
    buildLineRow(columns, {
      id: "Customer Provided LatLong used",
      address: template.address,
      latitude,
      longitude,
      ...template.defaultSettings,
      fireProtection: "flame",
    }) as PortfolioExportExampleBodyRow,
  ];

  for (const archetype of archetypes) {
    const index = lines.length;
    const { lat, lng } = gridCoordinates(template, index, archetypes.length);

    lines.push(archetypeRow(columns, archetype, { id: `Asset ${index}`, lat, lng, regionType }));
  }

  return lines;
};

/**
 * Global template: three lead rows covering address only, coordinates only and both,
 * then one row per archetype at its sample coordinate.
 */
const buildGlobalLines = (
  columns: PortfolioExportExampleKey[],
  archetypes: Archetype[],
  regionType: string | undefined,
  template: UploadTemplate
): PortfolioExportExampleLines => {
  // the region's elevation is cleared so the address-only lead row leaves it blank
  const thresholds = { ...template.defaultSettings, elevation: undefined };
  const { address, elevation } = portfolioTemplateLeadAsset;

  // The lead rows locate one asset three ways, using the first archetype. Fire protection and
  // foundation design differ per row to show the accepted values.
  const leadRowInputs = { ...archetypeInputs(archetypes[0], regionType), ...thresholds };
  const latitude = String(portfolioTemplateLeadAsset.lat);
  const longitude = String(portfolioTemplateLeadAsset.lng);

  const lines: PortfolioExportExampleLines = [
    headerRow(columns),
    buildLineRow(columns, {
      ...leadRowInputs,
      id: "Sample Asset 1_Address ONLY Provided",
      address,
      fireProtection: "none",
      foundationDesign: "normal",
    }) as PortfolioExportExampleBodyRow,
    buildLineRow(columns, {
      ...leadRowInputs,
      id: "Sample Asset 2_LatLong ONLY Provided",
      latitude,
      longitude,
      elevation,
      fireProtection: "heatEmber",
      foundationDesign: "rigid",
    }) as PortfolioExportExampleBodyRow,
    buildLineRow(columns, {
      ...leadRowInputs,
      id: "Sample Asset 3_BOTH Address & LatLong Provided",
      address,
      latitude,
      longitude,
      elevation,
      fireProtection: "flame",
    }) as PortfolioExportExampleBodyRow,
  ];

  archetypes.forEach((archetype, index) => {
    const exampleAssets = portfolioExportExampleAssets[archetype.value];
    const { lat, lng } = exampleAssets ?? gridCoordinates(template, index, archetypes.length);

    lines.push(
      archetypeRow(columns, archetype, {
        // the header offsets the 1-based numbering, so the pending length is this row's number
        id: `Sample Asset ${lines.length}_${categoryLabels[archetype.category]} - ${archetype.name}`,
        lat: String(lat),
        lng: String(lng),
        regionType,
        pricedAs: exampleAssets?.pricedAs,
      })
    );
  });

  return lines;
};

/**
 * Example structure of a .csv which can be uploaded to Portfolio Export.
 * @param selectedKeys - array of column headers, defaults to header name of id,addr,lat,lng
 * @returns array for csv
 */
export const buildPortfolioExportExampleCsv = (
  selectedColumns: PortfolioExportExampleKey[] = ["id", "address", "latitude", "longitude"],
  regionType?: string
): PortfolioExportExampleLines => {
  const {
    portfolioExport: { templates },
  } = getConfig();

  const archetypes = getArchetypes(regionType);
  const isHkma = getBaseThemeName() === "hkma";
  const template =
    regionType !== undefined && Object.hasOwn(templates, regionType)
      ? templates[regionType as keyof typeof templates]
      : templates[isHkma ? "hk" : "global"];

  // HKSAR keeps its original layout for both its regional and global templates.
  if (isHkma) return buildHkLines(selectedColumns, archetypes, regionType, template);

  const columns = [
    ...columnOrder.filter((column) => selectedColumns.includes(column)),
    // anything not yet ordered above still gets a column, on the end
    ...selectedColumns.filter((column) => !columnOrder.includes(column)),
  ];

  return buildGlobalLines(columns, archetypes, regionType, template);
};

/** Rows per page in the analysis list. */
export const ANALYSIS_PAGE_SIZE = 10;

export const portfolioExportSize = {
  1000: "1000",
  20000: "20k",
  55000: "55k",
  100000: "100k",
  120000: "100k+",
};

export type PortfolioExportSizeType = keyof typeof portfolioExportSize;
